import { styled } from '@mui/material/styles';
import { Button, Card, FormControl, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Shared styled components for the Manage releases / Manage templates cards
// and the reusable dialogs. Kept in one file so the section/heading/field
// rhythm stays consistent across both cards.

export const Section = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const Heading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

// Smaller font on the trigger so the sidebar reads as a compact control
// surface, but keep MUI's default small padding — the floating "Available
// releases" label needs vertical room above the value, unlike the State
// dropdown which has no label and can be squeezed tighter.
//
// The InputLabel override is paired with the InputBase one so the
// notched-outline legend (which measures its width from the *inherited*
// input font) stays the same size as the actually-rendered floating label.
// Without the pair, the legend renders narrower than the label and the
// label text overflows past the notch on the right edge.
//
// Backstage's createUnifiedTheme prefixes MUI v5 utility classes with
// `v5-`, so the descendant selectors target the prefixed forms.
export const Field = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(1),
  '& [class*="v5-MuiInputBase-root"]': {
    fontSize: '0.8rem',
  },
  '& [class*="v5-MuiInputLabel-outlined"]': {
    fontSize: '0.8rem',
  },
}));

export const ActionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '& > button': { flex: 1 },
}));

export const CollapsibleHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  userSelect: 'none',
});

export const CollapsibleHeaderTitle = styled(Typography)({
  marginBottom: 0,
});

export const Chevron = styled(ExpandMoreIcon, {
  shouldForwardProp: prop => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  transform: open ? 'rotate(180deg)' : 'none',
}));

// Outlined Button styled with the destructive palette. Hover fills the
// background with the error colour for the standard "danger primes"
// affordance.
export const DangerOutlinedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    borderColor: theme.palette.error.main,
  },
}));

// Contained Button using the destructive palette. Used by ConfirmDialog
// when the confirm action is destructive (Delete release / Delete backup).
export const DangerContainedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));
